import type { AppId } from 'constants/known-apps';

// Please adjust the spinner styles if our Spinner component is changed
// import { Spinner } from '@timelyapp/react';

// Launches popup window as promise, so you can do
// appAuthPopup.connect('google').then((appId) => { console.log('connected to', appId )});

// Calculates where we'd like the popup to appear,
// even on multi-monitor displays
interface Position {
  popupWidth: number;
  popupHeight: number;
  left: number;
  top: number;
}
const calculatePosition = (): Position => {
  const popupWidth = 960;
  const popupHeight = 640;

  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

  let screenWidth = 0;
  let screenHeight = 0;

  if (window && window.innerWidth) {
    screenWidth = window.innerWidth;
    screenHeight = window.innerHeight;
  } else if (document && document.documentElement) {
    screenWidth = document.documentElement.clientWidth;
    screenHeight = document.documentElement.clientHeight;
  }

  const left = screenWidth / 2 - popupWidth / 2 + dualScreenLeft;
  const top = screenHeight / 2 - popupHeight / 2 + dualScreenTop;

  return {
    popupWidth,
    popupHeight,
    left,
    top,
  };
};

const connect = (appId: AppId, url: string): Promise<any> => {
  const { popupWidth, popupHeight, left, top } = calculatePosition();
  const winOptions = [
    `width=${popupWidth}`,
    `height=${popupHeight}`,
    `left=${left}`,
    `top=${top}`,
    'titleBarStyle=default',
    'center=true',
    'title=Loading Service...',
  ].join(',');

  // All "local" server-side oAuth processeses use this approach:
  // Dodge popup blockes by re-routing after popup is opened:
  const popupWindow = window.open('about:blank', '_blank', winOptions);
  if (popupWindow) {
    popupWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Loading Service...</title>
          <style>
            :root {
              --uib-size: 48px;
              --uib-color: #666;
              --uib-speed: 0.9s;
            }

            body {
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: #f8f9fa;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              color: #333;
            }

            .dot-spinner-container {
              flex-shrink: 0;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              height: var(--uib-size);
              width: var(--uib-size);
              margin-bottom: 20px;
            }

            .dot-spinner-inner {
              position: relative;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              height: var(--uib-size);
              width: var(--uib-size);
            }

            .dot {
              position: absolute;
              top: 0;
              left: 0;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              height: 100%;
              width: 100%;
            }

            .dot::before {
              content: '';
              height: 20%;
              width: 20%;
              border-radius: 50%;
              background-color: var(--uib-color);
              transform: scale(0);
              opacity: 0.5;
              animation: dot-pulse calc(var(--uib-speed) * 1.111) ease-in-out infinite;
              transition: background-color 0.3s ease;
            }

            .dot:nth-child(1) { transform: rotate(0deg); }
            .dot:nth-child(1)::before { animation-delay: calc(var(--uib-speed) * -1.0); }
            .dot:nth-child(2) { transform: rotate(45deg); }
            .dot:nth-child(2)::before { animation-delay: calc(var(--uib-speed) * -0.875); }
            .dot:nth-child(3) { transform: rotate(90deg); }
            .dot:nth-child(3)::before { animation-delay: calc(var(--uib-speed) * -0.75); }
            .dot:nth-child(4) { transform: rotate(135deg); }
            .dot:nth-child(4)::before { animation-delay: calc(var(--uib-speed) * -0.625); }
            .dot:nth-child(5) { transform: rotate(180deg); }
            .dot:nth-child(5)::before { animation-delay: calc(var(--uib-speed) * -0.5); }
            .dot:nth-child(6) { transform: rotate(225deg); }
            .dot:nth-child(6)::before { animation-delay: calc(var(--uib-speed) * -0.375); }
            .dot:nth-child(7) { transform: rotate(270deg); }
            .dot:nth-child(7)::before { animation-delay: calc(var(--uib-speed) * -0.25); }
            .dot:nth-child(8) { transform: rotate(315deg); }
            .dot:nth-child(8)::before { animation-delay: calc(var(--uib-speed) * -0.125); }

            @keyframes dot-pulse {
              0%, 100% {
                transform: scale(0);
                opacity: 0.5;
              }
              50% {
                transform: scale(1);
                opacity: 1;
              }
            }

            .loading-text {
              font-size: 16px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="dot-spinner-container">
            <div class="dot-spinner-inner">
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
            </div>
          </div>
          <div class="loading-text">Connecting to service...</div>
        </body>
      </html>
    `);
    popupWindow.document.close();

    setTimeout(() => {
      popupWindow.location.href = url;
    }, 100);

    popupWindow.focus();
  }

  return new Promise(resolve => {
    // Detect when window closes
    // via http://stackoverflow.com/questions/3291712/
    const pollTimer = window.setInterval(() => {
      if (popupWindow && popupWindow.closed !== false) {
        window.clearInterval(pollTimer);
        return resolve(appId);
      }
      return false; // Popup still open
    }, 200);
  });
};

export default { connect, calculatePosition };
